import React, { useEffect, useState } from 'react';
import { message as antdMessage } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './TaskDetail.module.scss';
import EditTaskModal from '../../Components/Task/EditTaskModal';
import TaskChat from '../../Components/TaskChat/TaskChat';
import api from '../../services/api';
import LoadingOverlay from '../../Components/Loading/LoadingOverlay';
import MediaViewer from '../../Components/MediaViewer/MediaViewer';
import useTaskPresence from '../../hooks/useTaskPresence';
import InlineEditableField from '../../Components/InlineEditableField/InlineEditableField';
import TaskTagsEditor from '../../Components/TaskTagsEditor/TaskTagsEditor';
import DesignLayoutPreview from '../../Components/DesignLayoutPreview/DesignLayoutPreview';

const TASK_KIND_OPTIONS = [
    { value: 'common', label: 'Обычная' },
    { value: 'service_improvement', label: 'Доработка сервиса' },
];

const PRIORITY_OPTIONS = [
    { value: '4', label: 'Не важная' },
    { value: '2', label: 'Средняя' },
    { value: '3', label: 'Важная' },
    { value: '1', label: 'Критическая' },
];

const STAGE_OPTIONS = [
    { value: 'Свободно', label: 'Свободно' },
    { value: 'В работе', label: 'В работе' },
    { value: 'Требует внимание партнера', label: 'Требует внимание партнера' },
    { value: 'Закрыто', label: 'Закрыто' },
    { value: 'Отклонено', label: 'Отклонено' },
    { value: 'В архиве', label: 'В архиве' },
];

const ACTIVE_TERMINAL_LOG_REQUEST_STATUSES = new Set(['pending_dispatch', 'queued', 'processing']);

function terminalLogRequestStatusLabel(logRequest) {
    if (!logRequest) return '';
    if (logRequest.status === 'pending_dispatch' || logRequest.status === 'queued') return 'Ожидает терминал';
    if (logRequest.status === 'processing' && logRequest.stage === 'uploading') return 'Загрузка';
    if (logRequest.status === 'processing') return 'Упаковка';
    if (logRequest.status === 'ready') return 'Готов';
    if (logRequest.status === 'failed') return 'Ошибка';
    if (logRequest.status === 'expired') return 'Архив удалён';
    return logRequest.status || 'Неизвестно';
}

function formatFileSize(size) {
    const bytes = Number(size || 0);
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} КБ`;
    return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

function resolveDownloadFileName(headers, fallback) {
    const disposition = String(headers?.['content-disposition'] || '');
    const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (encodedMatch) {
        try {
            return decodeURIComponent(encodedMatch[1]);
        } catch {
            return fallback;
        }
    }
    const match = disposition.match(/filename="?([^";]+)"?/i);
    return match?.[1] || fallback;
}

function toSelectOptions(items) {
    return (Array.isArray(items) ? items : [])
        .map((item) => ({
            value: item.value ?? item.id,
            label: item.label ?? item.name ?? item.title ?? '',
        }))
        .filter((item) => item.value !== undefined && item.value !== null && item.label);
}

function ensureOption(options, value, label) {
    if (value === null || value === undefined || value === '') {
        return options;
    }

    if (options.some((option) => String(option.value) === String(value))) {
        return options;
    }

    return [{ value, label: label || String(value) }, ...options];
}

function optionLabel(options, value) {
    return options.find((option) => String(option.value) === String(value))?.label || '';
}

function getDescriptionValue(task) {
    const body = task?.body && typeof task.body === 'object' ? task.body : {};

    return body.problemDescription
        || body.text
        || body._descriptionText
        || body.description
        || body.message
        || body.task
        || '';
}

function buildDescriptionPayload(task, nextValue) {
    const body = task?.body && typeof task.body === 'object' ? { ...task.body } : {};
    if (Object.prototype.hasOwnProperty.call(body, 'problemDescription')) {
        body.problemDescription = nextValue;
    } else if (Object.prototype.hasOwnProperty.call(body, 'text')) {
        body.text = nextValue;
    } else if (Object.prototype.hasOwnProperty.call(body, '_descriptionText')) {
        body._descriptionText = nextValue;
    } else {
        body._descriptionText = nextValue;
    }

    return body;
}

function buildBodyFieldPayload(task, key, nextValue) {
    const body = task?.body && typeof task.body === 'object' ? { ...task.body } : {};
    body[key] = nextValue;

    return body;
}

function formatTerminalSoftwareVersion(snapshot) {
    const version = snapshot?.version || '';
    if (!version) {
        return '—';
    }

    const parts = [version];
    if (snapshot?.terminalKey) {
        parts.push(`терминал ${snapshot.terminalKey}`);
    }
    if (snapshot?.checkedAt) {
        parts.push(`проверено ${snapshot.checkedAt}`);
    }

    return parts.join(' · ');
}

function formatHistoryDetails(item) {
    const parts = [];
    if (item?.dateCreate) {
        parts.push(`Создано: ${item.dateCreate}`);
    }
    if (item?.terminalNumber) {
        parts.push(`Терминал: ${item.terminalNumber}`);
    }
    if (item?.contactPhone) {
        parts.push(`Телефон: ${item.contactPhone}`);
    }

    return parts.join(' · ');
}

function TaskRatingBlock({ rating, onSubmit, isSubmitting }) {
    if (!rating?.requested) {
        return null;
    }

    const score = Number(rating.score || 0);
    const canRate = Boolean(rating.canRate);
    const comment = typeof rating.comment === 'string' ? rating.comment.trim() : '';

    return (
        <div className={styles.ratingBox}>
            <div className={styles.ratingHeader}>
                <label>Оценка закрытия</label>
                {score > 0 ? <span>{score}/5</span> : <span>Ожидает оценки</span>}
            </div>
            {canRate ? (
                <div className={styles.ratingButtons}>
                    {[1, 2, 3, 4, 5].map((value) => (
                        <button
                            key={value}
                            type="button"
                            className={`${styles.ratingButton} ${score === value ? styles.ratingButtonActive : ''}`}
                            disabled={isSubmitting}
                            onClick={() => onSubmit(value)}
                        >
                            {value}
                        </button>
                    ))}
                </div>
            ) : score > 0 ? (
                <p className={styles.ratingReadonly}>Поставлена оценка {score}/5</p>
            ) : (
                <p className={styles.ratingReadonly}>Постановщик ещё не оценил закрытие</p>
            )}
            {comment ? (
                <p className={styles.ratingReadonly}><strong>Комментарий:</strong> {comment}</p>
            ) : null}
        </div>
    );
}

const TaskDetail = ({ taskId: taskIdOverride = null, embedded = false, onTaskUpdated = null, trackOpenOnLoad = true }) => {
    const { taskId: routeTaskId } = useParams();
    const navigate = useNavigate();
    const taskId = taskIdOverride || routeTaskId;
    const [task, setTask] = useState(null);
    const [supportContext, setSupportContext] = useState(null);
    const [taskAudit, setTaskAudit] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSupportContextLoading, setIsSupportContextLoading] = useState(false);
    const [isTaskAuditLoading, setIsTaskAuditLoading] = useState(false);
    const [supportContextError, setSupportContextError] = useState('');
    const [taskAuditError, setTaskAuditError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isRatingSubmitting, setIsRatingSubmitting] = useState(false);
    const [requestingTerminalKey, setRequestingTerminalKey] = useState('');
    const [downloadingLogRequestId, setDownloadingLogRequestId] = useState('');
    const [internalCommentDraft, setInternalCommentDraft] = useState('');
    const { activeViewers } = useTaskPresence(taskId);

    const fetchTask = async ({ trackOpen = true } = {}) => {
        if (!taskId) {
            setTask(null);
            setIsLoading(false);
            return;
        }
        try {
            setIsLoading(true);
            const suffix = trackOpen ? '' : '?trackOpen=0';
            const response = await api.get(`/api/getTask/${taskId}${suffix}`);
            setTask(response.data || null);
        } catch (error) {
            console.error('Ошибка при загрузке:', error);
            setTask(null);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTaskAudit = async () => {
        try {
            setIsTaskAuditLoading(true);
            setTaskAuditError('');
            const response = await api.get(`/api/task-audit/${taskId}`);
            setTaskAudit(response.data || null);
        } catch (error) {
            console.error('Ошибка при загрузке аудита задачи:', error);
            setTaskAudit(null);
            setTaskAuditError('Не удалось загрузить историю действий.');
        } finally {
            setIsTaskAuditLoading(false);
        }
    };

    const fetchSupportContext = async ({ silent = false } = {}) => {
        try {
            if (!silent) {
                setIsSupportContextLoading(true);
                setSupportContextError('');
            }
            const response = await api.get(`/api/task-support/${taskId}/context`);
            setSupportContext(response.data || null);
        } catch (error) {
            console.error('Ошибка при загрузке техконтекста:', error);
            if (!silent) {
                setSupportContext(null);
                setSupportContextError('Не удалось загрузить техконтекст задачи.');
            }
        } finally {
            if (!silent) {
                setIsSupportContextLoading(false);
            }
        }
    };

    useEffect(() => {
        let cancelled = false;

        if (!taskId) {
            setTask(null);
            setSupportContext(null);
            setTaskAudit(null);
            setIsLoading(false);
            return () => {
                cancelled = true;
            };
        }

        void (async () => {
            await fetchTask({ trackOpen: trackOpenOnLoad });
            if (cancelled) {
                return;
            }
            await Promise.all([fetchSupportContext(), fetchTaskAudit()]);
            try {
                await api.post('/api/notifications/mark-read', { taskId: Number(taskId) });
            } catch (error) {
                console.error('Не удалось отметить уведомления тикета как прочитанные:', error);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [taskId, trackOpenOnLoad]);

    useEffect(() => {
        setInternalCommentDraft(task?.localComment || '');
    }, [task?.id, task?.localComment]);

    const hasActiveTerminalLogRequest = (Array.isArray(supportContext?.terminals) ? supportContext.terminals : []).some((terminal) => (
        ACTIVE_TERMINAL_LOG_REQUEST_STATUSES.has(terminal?.logRequest?.status)
    ));

    useEffect(() => {
        if (!taskId || !hasActiveTerminalLogRequest) {
            return undefined;
        }

        const pollTimer = window.setInterval(() => {
            void fetchSupportContext({ silent: true });
        }, 10000);

        return () => window.clearInterval(pollTimer);
    }, [taskId, hasActiveTerminalLogRequest]);

    const handleRequestTerminalLogs = async (terminalKey) => {
        if (!terminalKey || requestingTerminalKey) return;

        try {
            setRequestingTerminalKey(terminalKey);
            const response = await api.post(`/api/task-support/${taskId}/terminal-log-requests`, {
                terminalKey,
            });
            setSupportContext((current) => {
                if (!current) return current;
                return {
                    ...current,
                    terminals: (current.terminals || []).map((terminal) => (
                        terminal.key === terminalKey ? { ...terminal, logRequest: response.data } : terminal
                    )),
                };
            });
            antdMessage.success('Запрос логов сохранён');
        } catch (error) {
            console.error('Ошибка при запросе логов терминала:', error);
            antdMessage.error('Не удалось запросить логи терминала');
        } finally {
            setRequestingTerminalKey('');
        }
    };

    const handleDownloadTerminalLogs = async (logRequest) => {
        if (!logRequest?.downloadUrl || downloadingLogRequestId) return;

        try {
            setDownloadingLogRequestId(logRequest.requestId);
            const response = await api.get(logRequest.downloadUrl, { responseType: 'blob' });
            const objectUrl = window.URL.createObjectURL(response.data);
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = resolveDownloadFileName(
                response.headers,
                logRequest.archiveName || `terminal-logs-${logRequest.requestId}.zip`
            );
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
        } catch (error) {
            console.error('Ошибка при скачивании логов терминала:', error);
            antdMessage.error('Не удалось скачать архив');
            await fetchSupportContext({ silent: true });
        } finally {
            setDownloadingLogRequestId('');
        }
    };

    const handleSaveTask = async (updatedTask) => {
        try {
            setIsSaving(true);
            const response = await api.put(`/tasks/${taskId}`, updatedTask);
            if (response.status >= 200 && response.status < 300) {
                await fetchTask({ trackOpen: false });
                await Promise.all([fetchSupportContext(), fetchTaskAudit()]);
                if (typeof onTaskUpdated === 'function') {
                    await onTaskUpdated();
                }
            }
        } catch (error) {
            console.error('Ошибка при сохранении задачи:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handlePatchTask = async (patch) => {
        try {
            setIsSaving(true);
            const response = await api.patch(`/tasks/${taskId}`, patch);
            if (response.status >= 200 && response.status < 300) {
                await fetchTask({ trackOpen: false });
                await Promise.all([fetchSupportContext(), fetchTaskAudit()]);
                if (typeof onTaskUpdated === 'function') {
                    await onTaskUpdated();
                }
                antdMessage.success('Тикет обновлён');
            }
        } catch (error) {
            console.error('Ошибка при обновлении задачи:', error);
            antdMessage.error('Не удалось обновить тикет');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSubmitRating = async (score) => {
        try {
            setIsRatingSubmitting(true);
            const response = await api.post(`/api/tasks/${taskId}/rating`, { score });
            if (response.data?.rating) {
                setTask((current) => current ? { ...current, rating: response.data.rating } : current);
            }
            await fetchTask({ trackOpen: false });
            if (typeof onTaskUpdated === 'function') {
                await onTaskUpdated();
            }
            antdMessage.success('Оценка сохранена');
        } catch (error) {
            console.error('Ошибка при сохранении оценки:', error);
            antdMessage.error('Не удалось сохранить оценку');
        } finally {
            setIsRatingSubmitting(false);
        }
    };

    const handleSaveInternalComment = async () => {
        const currentValue = task?.localComment || '';
        if (internalCommentDraft === currentValue) {
            return;
        }

        await handlePatchTask({ internalComment: internalCommentDraft });
    };

    const handleInternalCommentKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            setInternalCommentDraft(task?.localComment || '');
            return;
        }

        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            void handleSaveInternalComment();
        }
    };

    if (!taskId) {
        return (
            <div className={`${styles.container} ${embedded ? styles.embedded : ''}`}>
                <div className={styles.placeholder}>
                    Выберите тикет слева, чтобы открыть подробности.
                </div>
            </div>
        );
    }

    if (isLoading) {
        return <LoadingOverlay inline text="Загружаем задачу..." />;
    }

    if (!task) {
        return (
            <div className={`${styles.container} ${embedded ? styles.embedded : ''}`}>
                <div className={styles.placeholder}>
                    Не удалось загрузить задачу. Попробуйте выбрать тикет заново.
                </div>
            </div>
        );
    }

    const authorLabel = task.author?.label || task.authorDisplay || 'Не указан';
    const canLinkAuthor = Boolean(task.author?.label);
    const observers = Array.isArray(task.observers) ? task.observers : [];
    const taskTeamId = Number(task?.team?.id || supportContext?.teamId || 0);
    const techContextVisible = taskTeamId === 1 || Boolean(supportContext?.isApplicable);
    const aiEnabled = Boolean(supportContext?.aiEnabled);
    const aiInsight = supportContext?.aiInsight || null;
    const supportTerminals = Array.isArray(supportContext?.terminals) ? supportContext.terminals : [];
    const activity = Array.isArray(taskAudit?.activity) ? taskAudit.activity : [];
    const okkMediaUrl = task.body?.kind === 'okk' ? task.body?.imageUrl : '';
    const userOptions = toSelectOptions(task.users);
    const authorOptions = ensureOption(userOptions, task.author?.id, authorLabel);
    const assigneeOptions = ensureOption(toSelectOptions(task.userSpecialist), task.assignee?.id, task.assignee?.label);
    const observerOptions = userOptions;
    const observerIds = observers.map((observer) => observer.id).filter((id) => id !== undefined && id !== null);
    const observerLabel = observers.map((observer) => observer?.label || observer?.name).filter(Boolean).join(', ');
    const currentPriority = PRIORITY_OPTIONS.find((option) => option.label === task.priority || String(option.value) === String(task.priority));
    const currentPriorityValue = currentPriority?.value || task.priority || '';
    const taskKindLabel = optionLabel(TASK_KIND_OPTIONS, task.taskKind) || task.taskKindLabel || 'Обычная';
    const terminalSoftwareVersion = formatTerminalSoftwareVersion(task.terminalSoftwareVersion);
    const primaryDescription = getDescriptionValue(task);
    const addressHistory = Array.isArray(task.addressHistory) ? task.addressHistory : [];
    const addressHistoryAddress = task.addressHistoryAddress || task.body?.companyAddress || task.body?.address || supportContext?.taskAddress || '';
    const showAddressHistory = taskTeamId === 1;
    const internalCommentValue = task.localComment || '';
    const internalCommentChanged = internalCommentDraft !== internalCommentValue;

    return (
        <div className={`${styles.container} ${embedded ? styles.embedded : ''}`}>
            {isSaving && <LoadingOverlay text="Сохраняем изменения..." />}
            <div className={styles.layout}>
                <aside className={styles.leftPanel}>
                    <h1>
                        <InlineEditableField
                            value={task.title || ''}
                            type="textarea"
                            rows={2}
                            onSave={(value) => handlePatchTask({ title: String(value).trim() })}
                            viewClassName={styles.titleInline}
                        />
                    </h1>

                    {task.terminalPaymentBlocked || task.body?.terminalPaymentBlocked ? (
                        <div className={styles.terminalPaymentBadge}>Терминал не принимает оплаты</div>
                    ) : null}

                    <div className={styles.field}>
                        <label>Теги</label>
                        <TaskTagsEditor
                            value={task.tags}
                            onSave={(tags) => handlePatchTask({ tags })}
                        />
                    </div>

                    {task.canUseInternalMessages ? (
                        <div className={`${styles.field} ${styles.internalCommentField}`}>
                            <label htmlFor={`task-${taskId}-internal-comment`}>Внутренний комментарий по тикету</label>
                            <textarea
                                id={`task-${taskId}-internal-comment`}
                                className={styles.internalCommentTextarea}
                                value={internalCommentDraft}
                                rows={4}
                                placeholder="Напишите внутренний комментарий для команды"
                                onChange={(event) => setInternalCommentDraft(event.target.value)}
                                onKeyDown={handleInternalCommentKeyDown}
                            />
                            <div className={styles.internalCommentActions}>
                                <button
                                    type="button"
                                    className={styles.primarySmallButton}
                                    onClick={() => void handleSaveInternalComment()}
                                    disabled={!internalCommentChanged || isSaving}
                                >
                                    Сохранить
                                </button>
                                <button
                                    type="button"
                                    className={styles.secondarySmallButton}
                                    onClick={() => setInternalCommentDraft(internalCommentValue)}
                                    disabled={!internalCommentChanged || isSaving}
                                >
                                    Отменить
                                </button>
                            </div>
                        </div>
                    ) : null}

                    <div className={styles.field}>
                        <label>Автор задачи</label>
                        <InlineEditableField
                            value={task.author?.id || ''}
                            displayValue={authorLabel}
                            type="select"
                            options={authorOptions}
                            onSave={(value) => handlePatchTask({ author: value })}
                            disabled={!canLinkAuthor}
                        />
                    </div>

                    <div className={styles.field}>
                        <label>Менеджер</label>
                        <p>{task.manager || '—'}</p>
                    </div>

                    <div className={styles.field}>
                        <label>Номер задачи</label>
                        <p>{task.id}</p>
                    </div>

                    <div className={styles.field}>
                        <label>Команда</label>
                        <p>{task.team?.title || '—'}</p>
                    </div>

                    <div className={styles.field}>
                        <label>Тип задачи</label>
                        <InlineEditableField
                            value={task.taskKind || 'common'}
                            displayValue={taskKindLabel}
                            type="select"
                            options={TASK_KIND_OPTIONS}
                            onSave={(value) => handlePatchTask({ taskKind: value || 'common' })}
                        />
                    </div>

                    <div className={styles.field}>
                        <label>Номер терминала</label>
                        <InlineEditableField
                            value={task.terminalNumber || ''}
                            onSave={(value) => handlePatchTask({ terminalNumber: value })}
                        />
                        <div className={styles.softwareLine}>Версия ПО: {terminalSoftwareVersion}</div>
                    </div>

                    <div className={styles.field}>
                        <label>Телефон для связи</label>
                        <InlineEditableField
                            value={task.contactPhone || task.body?.contactPhone || ''}
                            onSave={(value) => handlePatchTask({ contactPhone: value })}
                        />
                    </div>

                    {showAddressHistory ? (
                        <div className={styles.field}>
                            <label>История обращений по адресу</label>
                            {addressHistoryAddress ? (
                                <>
                                    <p className={styles.addressHistoryAddress}>{addressHistoryAddress}</p>
                                    {addressHistory.length > 0 ? (
                                        <details className={styles.addressHistoryDropdown}>
                                            <summary>Найдено обращений: {addressHistory.length}</summary>
                                            <div className={styles.addressHistoryList}>
                                                {addressHistory.map((item) => {
                                                    const details = formatHistoryDetails(item);

                                                    return (
                                                        <button
                                                            key={item.id}
                                                            type="button"
                                                            className={styles.addressHistoryItem}
                                                            onClick={() => navigate(`/task/${item.id}`)}
                                                        >
                                                            <span className={styles.addressHistoryTopline}>
                                                                <strong>#{item.id}</strong>
                                                                <span className={`${styles.addressHistoryStatus} ${item.isClosed ? styles.addressHistoryStatusClosed : ''}`}>
                                                                    {item.status || '—'}
                                                                </span>
                                                            </span>
                                                            <span className={styles.addressHistoryTitle}>{item.title || 'Без названия'}</span>
                                                            {details ? (
                                                                <span className={styles.addressHistoryMeta}>{details}</span>
                                                            ) : null}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </details>
                                    ) : (
                                        <p className={styles.supportHint}>Обращений по этому адресу не найдено</p>
                                    )}
                                </>
                            ) : (
                                <p className={styles.supportHint}>Адрес не указан</p>
                            )}
                        </div>
                    ) : null}

                    <div className={styles.field}>
                        <label>Исполнитель</label>
                        <InlineEditableField
                            value={task.assignee?.id || ''}
                            displayValue={task.assignee?.label || '—'}
                            type="select"
                            options={assigneeOptions}
                            onSave={(value) => handlePatchTask({ assigneeId: value || null })}
                        />
                    </div>

                    <div className={styles.field}>
                        <label>Примерная дата решения</label>
                        <InlineEditableField
                            value={task.estimatedResolutionDate || ''}
                            displayValue={task.estimatedResolutionLabel || '—'}
                            type="date"
                            onSave={(value) => handlePatchTask({ estimatedResolutionDate: value || null })}
                        />
                    </div>

                    <div className={styles.field}>
                        <label>Наблюдатели</label>
                        <InlineEditableField
                            value={observerIds}
                            displayValue={observerLabel || '—'}
                            type="multiselect"
                            options={observerOptions}
                            onSave={(value) => handlePatchTask({ observers: Array.isArray(value) ? value : [] })}
                        />
                    </div>

                    <div className={styles.field}>
                        <label>Оплата на терминале заблокирована</label>
                        <InlineEditableField
                            value={Boolean(task.terminalPaymentBlocked || task.body?.terminalPaymentBlocked)}
                            type="checkbox"
                            onSave={(value) => handlePatchTask({ terminalPaymentBlocked: Boolean(value) })}
                        />
                    </div>

                    <div className={styles.field}>
                        <label>Сейчас смотрят</label>
                        {activeViewers.length > 0 ? (
                            <div className={styles.presenceList}>
                                {activeViewers.map((viewer) => (
                                    <div key={`${viewer.userId}-${viewer.state}`} className={styles.presenceItem}>
                                        <strong>{viewer.name || 'Сотрудник'}</strong>
                                        <span>
                                            {viewer.state === 'hidden'
                                                ? `На другой вкладке еще ${viewer.expiresInSeconds || 0} сек`
                                                : 'Открыл карточку'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p>Сейчас никто из коллег не просматривает этот тикет.</p>
                        )}
                    </div>

                    <div className={styles.field}>
                        <label>Описание задачи</label>
                        {task.body?.kind === 'okk' ? (
                            <>
                                {task.body?.text ? (
                                    <p>
                                        <strong>Текст:</strong>{' '}
                                        <InlineEditableField
                                            value={task.body.text}
                                            type="textarea"
                                            onSave={(value) => handlePatchTask({ description: buildDescriptionPayload(task, value) })}
                                        />
                                    </p>
                                ) : null}
                                {okkMediaUrl ? (
                                    <>
                                        <p><strong>Вложение:</strong> <a href={okkMediaUrl} target="_blank" rel="noreferrer">{okkMediaUrl}</a></p>
                                        <MediaViewer link={okkMediaUrl} alt={task.title} label="Открыть вложение" />
                                    </>
                                ) : null}
                            </>
                        ) : null}
                        {task.body?.taskType ? <p><strong>Тип:</strong> {task.body.taskType}</p> : null}
                        {task.body?.problemDescription ? (
                            <p>
                                <strong>Описание:</strong>{' '}
                                <InlineEditableField
                                    value={task.body.problemDescription}
                                    type="textarea"
                                    onSave={(value) => handlePatchTask({ description: buildDescriptionPayload(task, value) })}
                                />
                            </p>
                        ) : null}
                        {!task.body?.problemDescription && !task.body?.text && primaryDescription ? (
                            <p>
                                <strong>Описание:</strong>{' '}
                                <InlineEditableField
                                    value={primaryDescription}
                                    type="textarea"
                                    onSave={(value) => handlePatchTask({ description: buildDescriptionPayload(task, value) })}
                                />
                            </p>
                        ) : null}
                        {task.body?.layoutRequired ? <p><strong>Макет:</strong> {task.body.layoutRequired}</p> : null}
                        <DesignLayoutPreview text={task.body?.layoutRequired} />
                        {task.body?.layoutFormat ? <p><strong>Формат макета:</strong> {task.body.layoutFormat}</p> : null}
                        {task.body?.visualizationRequired ? <p><strong>Визуализация:</strong> {task.body.visualizationRequired}</p> : null}
                        {task.body?.address ? <p><strong>Адрес:</strong> {task.body.address}</p> : null}
                        {task.body?.vkLink ? <p><strong>Ссылка на VK:</strong> {task.body.vkLink}</p> : null}
                        {task.body?.comment ? (
                            <p>
                                <strong>Комментарий:</strong>{' '}
                                <InlineEditableField
                                    value={task.body.comment}
                                    type="textarea"
                                    onSave={(value) => handlePatchTask({ description: buildBodyFieldPayload(task, 'comment', value) })}
                                />
                            </p>
                        ) : null}
                        {task.body?.linkRecord ? <p><strong>Ссылка на запись:</strong> {task.body.linkRecord}</p> : null}
                    </div>

                    {techContextVisible ? (
                        <>
                            {aiEnabled ? (
                                <div className={styles.field}>
                                    <label>AI-сводка по задаче</label>
                                    {isSupportContextLoading ? (
                                        <p>Обновляем сводку...</p>
                                    ) : supportContextError ? (
                                        <p className={styles.supportHint}>{supportContextError}</p>
                                    ) : aiInsight ? (
                                        <div className={styles.supportCard}>
                                            {aiInsight.summary ? <p><strong>Кратко:</strong> {aiInsight.summary}</p> : null}
                                            {aiInsight.currentStatus ? <p><strong>Статус:</strong> {aiInsight.currentStatus}</p> : null}
                                            {aiInsight.completionStatus ? <p><strong>Выполнение:</strong> {aiInsight.completionStatus}</p> : null}
                                            {aiInsight.openedAgo ? (
                                                <p>
                                                    <strong>Открыта:</strong> {aiInsight.openedAgo}
                                                    {aiInsight.openedAt ? ` (с ${aiInsight.openedAt})` : ''}
                                                </p>
                                            ) : null}
                                            {aiInsight.promiseStatus ? <p><strong>Обещание:</strong> {aiInsight.promiseStatus}</p> : null}
                                            {aiInsight.promiseOverdueBy ? <p><strong>Нарушение обещания:</strong> {aiInsight.promiseOverdueBy}</p> : null}
                                            {aiInsight.responsibleSide ? <p><strong>Кто должен действовать:</strong> {aiInsight.responsibleSide}</p> : null}
                                            {aiInsight.nextAction ? <p><strong>Следующий шаг:</strong> {aiInsight.nextAction}</p> : null}
                                            {aiInsight.stoppedAt ? <p><strong>На чем остановились:</strong> {aiInsight.stoppedAt}</p> : null}
                                            {aiInsight.blockers ? <p><strong>Блокеры:</strong> {aiInsight.blockers}</p> : null}
                                            {aiInsight.confidence ? <p><strong>Уверенность:</strong> {aiInsight.confidence}</p> : null}
                                        </div>
                                    ) : (
                                        <p>AI-сводка пока не сформирована.</p>
                                    )}
                                </div>
                            ) : null}

                            <div className={styles.field}>
                                <label>Контекст терминалов</label>
                                {supportContextError ? (
                                    <p className={styles.supportHint}>{supportContextError}</p>
                                ) : (
                                    <>
                                        <div className={styles.supportCard}>
                                            <p><strong>Адрес из задачи:</strong> {supportContext?.taskAddress || task.body?.address || '—'}</p>
                                            <p><strong>Компания:</strong> {supportContext?.company?.fullAddress || 'Не найдена'}</p>
                                            <p><strong>Последняя транзакция:</strong> {supportContext?.lastTransactionCreatedAt || '—'}</p>
                                        </div>
                                        {supportTerminals.length > 0 ? (
                                            <div className={styles.terminalList}>
                                                {supportTerminals.map((terminal) => (
                                                    <div key={terminal.key || terminal.id} className={styles.terminalCard}>
                                                        <p><strong>Терминал:</strong> {terminal.key || '—'}</p>
                                                        {terminal.title ? <p><strong>Название:</strong> {terminal.title}</p> : null}
                                                        <p><strong>Версия ПО:</strong> {terminal.softwareVersion || '—'}</p>
                                                        <p><strong>Версия проверена:</strong> {terminal.softwareVersionCheckedAt || '—'}</p>
                                                        <p><strong>AnyDesk ID:</strong> {terminal.anydeskId || 'Не найден'}</p>
                                                        <p><strong>Пароль AnyDesk:</strong> {terminal.anydeskPassword || 'Не найден'}</p>
                                                        {terminal.anydeskSyncedAt ? <p><strong>Синхронизировано:</strong> {terminal.anydeskSyncedAt}</p> : null}
                                                        {supportContext?.canRequestTerminalLogs ? (
                                                            <div className={styles.terminalLogRequest}>
                                                                {terminal.logRequest ? (
                                                                    <>
                                                                        <div className={styles.terminalLogRequestHeader}>
                                                                            <strong>Логи</strong>
                                                                            <span
                                                                                className={`${styles.terminalLogStatus} ${
                                                                                    terminal.logRequest.status === 'ready'
                                                                                        ? styles.terminalLogStatusReady
                                                                                        : terminal.logRequest.status === 'failed' || terminal.logRequest.status === 'expired'
                                                                                            ? styles.terminalLogStatusError
                                                                                            : styles.terminalLogStatusActive
                                                                                }`}
                                                                            >
                                                                                {terminalLogRequestStatusLabel(terminal.logRequest)}
                                                                            </span>
                                                                        </div>
                                                                        <p>
                                                                            Запрос: {terminal.logRequest.createdAt || '—'} ·{' '}
                                                                            {terminal.logRequest.requestedBy || 'Система'}
                                                                        </p>
                                                                        {terminal.logRequest.status === 'ready' ? (
                                                                            <p>
                                                                                {terminal.logRequest.fileCount || 0} файл(а)
                                                                                {terminal.logRequest.fileSize ? ` · ${formatFileSize(terminal.logRequest.fileSize)}` : ''}
                                                                                {terminal.logRequest.archiveExpiresAt ? ` · хранится до ${terminal.logRequest.archiveExpiresAt}` : ''}
                                                                            </p>
                                                                        ) : null}
                                                                        {terminal.logRequest.errorMessage ? (
                                                                            <p className={styles.terminalLogError}>{terminal.logRequest.errorMessage}</p>
                                                                        ) : null}
                                                                    </>
                                                                ) : (
                                                                    <p className={styles.supportHint}>Логи ещё не запрашивали.</p>
                                                                )}
                                                                <div className={styles.terminalLogActions}>
                                                                    {terminal.logRequest?.status === 'ready' && terminal.logRequest.downloadUrl ? (
                                                                        <button
                                                                            type="button"
                                                                            className={styles.primarySmallButton}
                                                                            disabled={downloadingLogRequestId === terminal.logRequest.requestId}
                                                                            onClick={() => void handleDownloadTerminalLogs(terminal.logRequest)}
                                                                        >
                                                                            {downloadingLogRequestId === terminal.logRequest.requestId ? 'Скачивание...' : 'Скачать ZIP'}
                                                                        </button>
                                                                    ) : null}
                                                                    <button
                                                                        type="button"
                                                                        className={styles.secondarySmallButton}
                                                                        disabled={
                                                                            !terminal.key
                                                                            || requestingTerminalKey === terminal.key
                                                                            || ACTIVE_TERMINAL_LOG_REQUEST_STATUSES.has(terminal.logRequest?.status)
                                                                        }
                                                                        onClick={() => void handleRequestTerminalLogs(terminal.key)}
                                                                    >
                                                                        {requestingTerminalKey === terminal.key
                                                                            ? 'Отправка...'
                                                                            : ACTIVE_TERMINAL_LOG_REQUEST_STATUSES.has(terminal.logRequest?.status)
                                                                                ? 'Запрос выполняется'
                                                                                : terminal.logRequest
                                                                                    ? 'Запросить повторно'
                                                                                    : 'Запросить последние 4 лога'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className={styles.supportHint}>Активные терминалы по компании не найдены.</p>
                                        )}
                                    </>
                                )}
                            </div>
                        </>
                    ) : null}

                    <div className={styles.field}>
                        <label>История действий</label>
                        {isTaskAuditLoading ? (
                            <p>Загружаем историю...</p>
                        ) : taskAuditError ? (
                            <p className={styles.supportHint}>{taskAuditError}</p>
                        ) : activity.length > 0 ? (
                            <div className={styles.auditList}>
                                {activity.map((item) => (
                                    <div key={item.id} className={styles.auditItem}>
                                        <p><strong>{item.actorName || 'Система'}</strong> · {item.actionLabel || item.actionType}</p>
                                        {item.description ? <p>{item.description}</p> : null}
                                        <p>{item.createdAt || '—'}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p>История действий по тикету пока пуста.</p>
                        )}
                    </div>

                    <div className={styles.field}>
                        <label>Важность</label>
                        <InlineEditableField
                            value={currentPriorityValue}
                            displayValue={task.priority || '—'}
                            type="select"
                            options={PRIORITY_OPTIONS}
                            onSave={(value) => handlePatchTask({ priority: value })}
                        />
                    </div>

                    <div className={styles.field}>
                        <label>Статус</label>
                        <InlineEditableField
                            value={task.status || ''}
                            type="select"
                            options={STAGE_OPTIONS}
                            onSave={(value) => handlePatchTask({ stage: value })}
                        />
                    </div>

                    <TaskRatingBlock
                        rating={task.rating}
                        isSubmitting={isRatingSubmitting}
                        onSubmit={handleSubmitRating}
                    />

                    <div className={styles.field}>
                        <label>Комментарий по партнёру</label>
                        <InlineEditableField
                            value={task.partnerLocalComment || ''}
                            type="textarea"
                            onSave={(value) => handlePatchTask({ partnerLocalComment: value })}
                            placeholder="Пока никто не оставил комментарий."
                        />
                    </div>

                    <div className={styles.buttonsContainer}>
                        <button onClick={() => setIsEditModalOpen(true)}>Редактировать</button>
                        {!embedded ? (
                            <button onClick={() => navigate('/board')}>Назад к доске</button>
                        ) : null}
                    </div>
                </aside>

                <section className={styles.rightPanel}>
                    <TaskChat
                        templatesResponse={task.templates}
                        taskId={taskId}
                        mentionCandidates={task.mentionCandidates}
                        onMessagesChanged={async () => {
                            await fetchTask({ trackOpen: false });
                            await Promise.all([fetchSupportContext(), fetchTaskAudit()]);
                            if (typeof onTaskUpdated === 'function') {
                                await onTaskUpdated();
                            }
                        }}
                    />
                </section>
            </div>

            <EditTaskModal
                isOpen={isEditModalOpen}
                onRequestClose={() => setIsEditModalOpen(false)}
                task={task}
                onSaveTask={handleSaveTask}
                canUseInternalMessages={Boolean(task.canUseInternalMessages)}
            />
        </div>
    );
};

export default TaskDetail;
